/**
 * SSE event names — single source of truth shared by backend emitters and
 * frontend listeners. Adding a new event? Add a key here, then both ends
 * import it. Catches typos at compile time and surfaces the full event
 * surface in one place.
 *
 * Use via:
 *   import { SSE_EVENTS } from "@lexicanon/shared"
 *   emitSSE(SSE_EVENTS.PROGRESSIVE_SUMMARY, meetingId, payload)
 *   if (msg.type === SSE_EVENTS.PROCESSING_STAGE) { … }
 */
export const SSE_EVENTS = {
	// Pipeline orchestration — graph executor emits these on every step run.
	PIPELINE_STARTED: "pipeline_started",
	PIPELINE_STEP: "pipeline_step",
	PROCESSING_STAGE: "processing_stage",

	// Streaming session output.
	PROGRESSIVE_SUMMARY: "progressive_summary",

	// Streaming session lifecycle (live transcription socket health).
	STREAMING_STARTED: "streaming_started",
	STREAMING_STOPPED: "streaming_stopped",
	STREAMING_RESUMED: "streaming_resumed",
	STREAMING_RECONNECTING: "streaming_reconnecting",
	STREAMING_FALLBACK: "streaming_fallback",
	STREAMING_ERROR: "streaming_error",
	STREAMING_SEGMENT: "streaming_segment",
	STREAMING_INTERIM: "streaming_interim",

	// Live meeting-language watch (quiet hint, one-shot suggestion, committed flip).
	LANGUAGE_SIGNAL: "language_signal",
	LANGUAGE_SUGGESTION: "language_suggestion",
	MEETING_LANGUAGE_CHANGED: "meeting_language_changed",

	// Image / visual generation.
	VISUAL_REUSED: "visual_reused",
	VISUAL_GENERATED: "visual_generated",
	VISUAL_PROGRESSIVE: "visual_progressive",
	IMAGE_REVEALED: "image_revealed",

	// Speaker identity (live + post-pipeline).
	SPEAKERS_IDENTIFIED: "speakers_identified",
	SPEAKER_IDENTIFIED: "speaker_identified",
	SPEAKER_HYPOTHESIS: "speaker_hypothesis",
	/**
	 * Broadcast after ANY speaker mutation (rename, discard, profile edit,
	 * hypothesis confirm/reject, pipeline resolution). Thin payload
	 * `{ speakers_version, reason }` — the frontend invalidates its meeting +
	 * transcript queries on receipt so every surface reconverges without a reload.
	 */
	SPEAKERS_UPDATED: "speakers_updated",

	// Voxcanon-only DM / player feedback (per-user channels).
	DM_SUGGESTION: "dm_suggestion",
	DM_PARTICIPATION: "dm_participation",
	DM_COACHING_REQUEST: "dm_coaching_request",
	PLAYER_COACHING: "player_coaching",

	// World-state / canon proposals raised during analysis.
	WORLD_PROPOSED: "world_proposed",
	CANON_PROPOSED: "canon_proposed",

	// Chapter detection.
	CHAPTERS_READY: "chapters_ready",

	// Domain-jargon candidates surfaced for review after analysis.
	JARGON_SUGGESTED: "jargon_suggested",

	// On-demand visual-report generation lifecycle.
	REPORT_GENERATION_STARTED: "report_generation_started",
	REPORT_GENERATION_STEP: "report_generation_step",
	REPORT_GENERATION_COMPLETE: "report_generation_complete",
	REPORT_GENERATION_FAILED: "report_generation_failed",
} as const;

export type TSseEventName = (typeof SSE_EVENTS)[keyof typeof SSE_EVENTS];
